/**
 * The report's vocabulary of small parts. Three colours carry everything (white, ink,
 * indigo), so status is drawn with fill, weight and an icon rather than hue.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** A titled block of the report. `aside` sits opposite the title for a count or a control. */
export const ReportSection = ({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) => (
  <section className='flex flex-col gap-2.5'>
    <div className='flex items-baseline justify-between gap-3'>
      <h3 className='text-[11px] font-medium tracking-wide text-gray-800 uppercase'>{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

type ChipTone = 'solid' | 'outline' | 'accent';

const CHIP_TONES: Record<ChipTone, string> = {
  solid: 'border-ink bg-ink text-white',
  outline: 'border-gray-500 bg-white text-text-body',
  accent: 'border-indigo-200 bg-indigo-50 text-indigo-700',
};

export const Chip = ({
  tone = 'outline',
  mono,
  children,
  className,
}: {
  tone?: ChipTone;
  mono?: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-4',
      CHIP_TONES[tone],
      mono && 'font-mono text-[10px]',
      className
    )}
  >
    {children}
  </span>
);

/**
 * Magnitude as filled dots rather than a colour word. `none` is a real answer on an
 * inconclusive verdict and fills nothing; defaulting an unknown severity to one dot would
 * draw it as minor.
 */
const SEVERITY_STEPS: Record<string, number> = { none: 0, minor: 1, major: 2, critical: 3 };

export const SeverityDots = ({ severity }: { severity: string }) => {
  const filled = SEVERITY_STEPS[severity.toLowerCase()] ?? 0;
  return (
    <span className='inline-flex items-center gap-1.5' title={`Severity: ${severity}`}>
      <span className='flex items-center gap-0.5' aria-hidden>
        {[1, 2, 3].map((step) => (
          <span key={step} className={cn('size-1.5 rounded-full', step <= filled ? 'bg-ink' : 'bg-gray-500')} />
        ))}
      </span>
      <span className='text-[11px] capitalize text-text-body'>{severity}</span>
    </span>
  );
};

/**
 * Staggered entrance: each child fades up a beat after the one above. `fill-mode-backwards`
 * keeps a delayed child hidden until its turn, and the delays are literal class names because
 * Tailwind reads them from source; a longer panel shares the last beat.
 */
const STAGGER_DELAYS = ['delay-0', 'delay-75', 'delay-150', 'delay-200', 'delay-300', 'delay-500'];

export const Stagger = ({ children, className }: { children: ReactNode[]; className?: string }) => (
  <div className={cn('flex flex-col', className)}>
    {children.filter(Boolean).map((child, index) => (
      <div
        key={index}
        className={cn(
          'animate-in fade-in-0 slide-in-from-bottom-2 duration-500 ease-out fill-mode-backwards',
          STAGGER_DELAYS[Math.min(index, STAGGER_DELAYS.length - 1)]
        )}
      >
        {child}
      </div>
    ))}
  </div>
);
